// LIBRARIES
import React, { useEffect, useState } from "react";
import { useT } from "@transifex/react";
import { useTestCreationContext } from "../../../context/TestCreationContext";
import { useRTContext } from "../../../context/RTContext";
import { useHistory } from "react-router-dom";
import StyledButton from "../../../basic/styledButton/StyledButton";
import ResponsiveButton from "../../../basic/responsiveButton/ResponsiveButton";
import SelectSearch, { fuzzySearch } from "react-select-search";
import { P6uBand, P6uPublisherBook } from "p6m-p6u";
import { useGeneralContext } from "../../../context/AppGeneralContext";
import { useLogger } from "../../../hooks/useLogger";
import { useConfirmBook } from "../../../hooks/useConfirmBook";
import { CreationModes } from "../../../constants/CreationFlowConstants";
import { postRequest } from "../../../helpers/networkHelper";
import NoOwnContentModal from "../../../basic/create/noOwnContentModal/NoOwnContentModal";
import { AxiosResponse } from "axios";

import CreationFlowSection from "../../../basic/create/creationFlowSection/CreationFlowSection";
import VocabTrainingTitleBar from "../../../complex/create/vocabTrainingTitleBar/VocabTrainingTitleBar";
import CtasWrapperRT from "../../../wrapper/create/ctasWrapperRT/ctasWrapperRT";

import {
    Wrapper,
    MiddleContentWrapper,
    SelectsWrapper,
    SelectBookBtn,
    SmallSelectWrapper,
    BookSearchSelectWrapper,
    BookInfoWrapper,
    BookImageWrapper,
    BookNameWrapper,
    BookName,
    SelectedContentWrapper,
    AdditionalInfoWrapper,
    WarningIconWrapper,
    StyledP6Icon,
    StyledCoverImage,
    BookAvailabilityInfoTitle,
    BookAvailabilityInfoDescWrapper,
    BookAvailabilityInfoDesc,
    StyledSignUpButton,
    TestToText,
    AdditionalInfoItem,
} from "./StyledComponents";
import useBookDropdownOptions from "../selectBookDropdowns/useBookDropdownOptions";

/** The vocab training's book selection before the redesign: the "dropdowns" variant of the book selection experiment. */
const SelectBookDropdownsRT = () => {
    const {
        openWarningModal,
        selectedBookData,
        userTests,
        decideGoToOwnSubjectsAction,
        userOwnSubjects,
        openWebAppInAddCardsMode,
        clearSchoolbookSelection,
    } = useTestCreationContext();

    const {
        loadAvailableSubjects,
        availableSubjects,
        loadPublisherBooksForSubject,
        availablePublishersAndBooks,
        loadBandsForBook,
        availableBands,
        setAvailablePublishersAndBooks,
        setAvailableBands,
    } = useBookDropdownOptions();

    const { currentExerciseSchoolClassId } = useRTContext();

    const { creatingMode, userInfo, setUserInfo } = useGeneralContext();
    const { logEvent } = useLogger();
    const confirmBook = useConfirmBook();

    const t = useT();
    const t_selectTitle = t("Please select a book", { _tags: "CreateTest,SelectBook" });
    const t_selectBook = t("Select Book", { _tags: "CreateTest,SelectBook" });
    const t_back = t("Back", { _tags: "CreateTest,SelectBook" });
    const t_useOwnContent = t("Use my own content", { _tags: "CreateTest,SelectBook" });
    const t_useOwnContentInfo = t("Use your own vocabulary in the meantime.", { _tags: "CreateTest,SelectBook" });
    const t_ownContentAnonymous = t("After registering, you can also use your own vocabulary", {
        _tags: "CreateTest,SelectBook",
    });
    const t_searchBook = t("Search Book", { _tags: "CreateTest,SelectBook" });
    const t_noResults = t("No Results", { _tags: "CreateTest,SelectBook" });
    const t_pleaseSelectLanguage = t("Please select first a language", { _tags: "CreateTest,SelectBook" });
    const t_notAllBookTitle = t("This function is currently available for almost all books", {
        _tags: "CreateTest,SelectBook",
    });
    const t_notAvailableBook = t(
        "Can't find your book? We will let you know through our teachers Newsletter when we have new content available.",
        { _tags: "CreateTest,SelectBook" }
    );
    const t_notAvailableBookNotRegistered = t(
        "Can't find your book? We will let you know through our teachers Newsletter when we have new content available. The subscription to the Newsletter is only possible after registration or login.",
        { _tags: "CreateTest,SelectBook" }
    );
    const t_signUp = t("Sign up now!", { _tags: "CreateTest,SelectBook" });
    const t_testTo = t("Test to", { _tags: "CreateTest,SelectVocabularySource" });
    const t_selectLanguage = t("Select a Language", { _tags: "CreateTest,SelectVocabularySource" });
    const t_selectBand = t("Select a Band", { _tags: "CreateTest,SelectVocabularySource" });
    const t_errorNewsletter = t("Error while subscribing to the Newsletter, please try later", {
        _tags: "CreateTest,SelectVocabularySource",
    });
    const t_unconfirmedNewsletter = t(
        "A step is missing! To complete your subscription to the newsletter, please check your email!",
        { _tags: "CreateTest,SelectVocabularySource" }
    );
    const t_continue = t("Continue", { _tags: "CreateTest,SelectVocabularySource" });

    const [selectedSubject, setSelectedSubject] = useState("");
    const [selectedPublisherBook, setSelectedPublisherBook] = useState("");
    const [selectedBand, setSelectedBand] = useState("");

    const [selectedPublisherBookInfo, setSelectedPublisherBookInfo] = useState<P6uPublisherBook>({});
    const [selectedBandInfo, setSelectedBandInfo] = useState<P6uBand>({});

    const [searchBookPlaceholder, setSearchBookPlaceholder] = useState("");
    const [selectBandPlaceholder, setSelectBandPlaceholder] = useState("");
    const [shouldInitPublisherBook, setShouldInitPublisherBook] = useState(false);
    const [shouldInitBand, setShouldInitBand] = useState(false);

    const [isNoOwnContentModalOpen, setIsNoOwnContentModalOpen] = useState(false);
    const [needsSubjectsReload, setNeedsSubjectsReload] = useState(false);

    const history = useHistory();

    useEffect(() => {
        if (availableSubjects.length === 0) {
            loadAvailableSubjects();
        }

        // Init when selected a book already
        if (selectedBookData) {
            if (selectedBookData.subject?.ID) {
                setSelectedSubject(selectedBookData.subject.ID);

                if (selectedBookData.publisherBook?.CombinedID) {
                    setShouldInitPublisherBook(true);

                    if (selectedBookData.band?.UUID) {
                        setShouldInitBand(true);
                    }
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (shouldInitPublisherBook && availablePublishersAndBooks.length > 0) {
            setSelectedPublisherBook(selectedBookData.publisherBook!.CombinedID!);
            setSelectedPublisherBookInfo(selectedBookData.publisherBook!);
            setShouldInitPublisherBook(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldInitPublisherBook, availablePublishersAndBooks]);

    useEffect(() => {
        if (shouldInitBand && availableBands.length > 0) {
            setSelectedBand(selectedBookData.band!.UUID!);
            setSelectedBandInfo(selectedBookData.band!);
            setShouldInitBand(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldInitBand, availableBands]);

    useEffect(() => {
        if (selectedSubject && selectedSubject !== "") {
            loadPublisherBooksForSubject(selectedSubject);
            setSearchBookPlaceholder(t_searchBook);
        } else {
            setSearchBookPlaceholder("");
        }
        setSelectBandPlaceholder("");
        setAvailablePublishersAndBooks([]);
        setAvailableBands([]);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSubject]);

    useEffect(() => {
        if (selectedSubject && selectedPublisherBook && selectedPublisherBook !== "") {
            loadBandsForBook(selectedSubject, selectedPublisherBook);
            setSelectBandPlaceholder(t_selectBand);
        } else {
            setAvailablePublishersAndBooks([]);
            setSelectBandPlaceholder("");
        }
        setAvailableBands([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPublisherBook]);

    const updateSubject = (selectedValue: any) => {
        setSelectedSubject(selectedValue as string);
        setSelectedPublisherBook("");
        setSelectedBand("");
        setSelectedBandInfo({});
        setSelectedPublisherBookInfo({});
        logEvent("Subject Selected", {
            subject: availableSubjects.find((s) => s.ID === selectedValue)?.Name,
            subject_id: selectedValue,
        });
    };

    const updatePublisherBook = (selectedValue: any) => {
        const selectedBook = availablePublishersAndBooks.find((pb) => pb.CombinedID === selectedValue);
        setSelectedPublisherBook(selectedValue as string);
        setSelectedBand("");
        setSelectedBandInfo({});
        setSelectedPublisherBookInfo(selectedBook ?? {});
        logEvent("Book Selected", { book_id: selectedValue, publisher: selectedBook?.PublisherName });
    };

    const updateBand = (selectedValue: any) => {
        const selectedBand = availableBands.find((b) => b.UUID === selectedValue);
        setSelectedBand(selectedValue as string);
        setSelectedBandInfo(selectedBand ?? {});
        logEvent("Band Selected", { band: selectedBand?.ShortName ?? selectedBand?.Name, band_id: selectedBand?.UUID });
    };

    function continueToVocabularySelection() {
        confirmBook({
            band: selectedBandInfo,
            publisherBook: selectedPublisherBookInfo,
            subject: availableSubjects.find((s) => s.ID === selectedSubject),
        });
    }

    function handleBackButton() {
        if (creatingMode === CreationModes.RECURRING_TASK) {
            history.push("/create/choose-vocab-training-source");
        } else {
            history.goBack();
        }
    }

    async function signupToNewsletter() {
        try {
            const subscriptionResponse: AxiosResponse<{ isUnconfirmed: boolean; needsEvent: boolean }> =
                await postRequest("aea/user/newsletter/");

            let subscriptionStatus;

            if (subscriptionResponse.data.hasOwnProperty("error")) {
                openWarningModal(t_errorNewsletter);
                subscriptionStatus = "Error";
            } else {
                if (subscriptionResponse.data.isUnconfirmed) {
                    openWarningModal(t_unconfirmedNewsletter);
                    subscriptionStatus = "unconfirmed";
                } else {
                    openWarningModal("Sie haben unseren Newsletter erfolgreich abonniert.");
                    setUserInfo({ ...userInfo, hasNewsletterSubscription: true });
                    subscriptionStatus = "confirmed";
                }
            }

            logEvent("VTG - Subscribe to newsletter", {
                subscriptionStatus,
            });

            if (subscriptionResponse.data.needsEvent) {
                logEvent("newsletterSubscription");
            }
        } catch (e) {
            openWarningModal(t_errorNewsletter);
        }
    }

    async function handleContinueOwnSubjectsClick() {
        let action = await decideGoToOwnSubjectsAction(needsSubjectsReload);
        if (action === "CONTINUE") {
            history.push("/create/select-own-source");
        } else if (action === "POPUP") {
            setIsNoOwnContentModalOpen(true);
        }
    }

    function handleGoToWebAppClick() {
        openWebAppInAddCardsMode();
        setIsNoOwnContentModalOpen(false);
        setNeedsSubjectsReload(true);
    }

    return (
        <Wrapper>
            <CreationFlowSection>
                <VocabTrainingTitleBar />
            </CreationFlowSection>
            <p className="px-s py-s text-l text-p6-grey-dark">{t_selectTitle}</p>
            <MiddleContentWrapper>
                <SelectsWrapper>
                    <SmallSelectWrapper data-testid="select-subject">
                        <SelectSearch
                            options={availableSubjects.map((s) => {
                                return { name: s.Name ?? "", value: s.ID ?? "" };
                            })}
                            search
                            filterOptions={fuzzySearch}
                            emptyMessage={t_noResults}
                            placeholder={t_selectLanguage}
                            value={selectedSubject}
                            onChange={updateSubject}
                        />
                    </SmallSelectWrapper>
                    <BookSearchSelectWrapper data-testid="select-publisher-book">
                        <SelectSearch
                            options={availablePublishersAndBooks.map((s) => {
                                return { name: s.DisplayName ?? "", value: s.CombinedID ?? "" };
                            })}
                            search
                            filterOptions={fuzzySearch}
                            placeholder={searchBookPlaceholder}
                            value={selectedPublisherBook}
                            onChange={updatePublisherBook}
                            disabled={!selectedSubject}
                            emptyMessage={availablePublishersAndBooks.length > 0 ? t_noResults : t_pleaseSelectLanguage}
                        />
                    </BookSearchSelectWrapper>
                    <SmallSelectWrapper data-testid="select-band">
                        <SelectSearch
                            options={availableBands.map((s) => {
                                return { name: s.ShortName || s.Name || "", value: s.UUID ?? "" };
                            })}
                            search
                            filterOptions={fuzzySearch}
                            emptyMessage={t_noResults}
                            disabled={!selectedPublisherBook}
                            value={selectedBand}
                            onChange={updateBand}
                            placeholder={selectBandPlaceholder}
                        />
                    </SmallSelectWrapper>
                </SelectsWrapper>

                <SelectedContentWrapper>
                    {selectedBandInfo && selectedBandInfo.UUID && (
                        <BookInfoWrapper>
                            <BookImageWrapper>
                                <StyledCoverImage imageId={"https://www.phase-6.de" + selectedBandInfo.Image} />
                            </BookImageWrapper>
                            <BookNameWrapper>
                                <TestToText>{t_testTo}</TestToText>
                                <BookName>
                                    {selectedPublisherBookInfo.BookName} ({selectedPublisherBookInfo.PublisherName}) (
                                    {selectedBandInfo.ShortName})
                                </BookName>
                            </BookNameWrapper>
                            <SelectBookBtn
                                data-testid="button-select-book"
                                icon={"library"}
                                iconColor={"white"}
                                iconPosition={"LEFT"}
                                onClick={continueToVocabularySelection}
                                arrowDirection={"RIGHT"}
                                buttonStyle={"PRIMARY"}
                            >
                                {t_selectBook}
                            </SelectBookBtn>
                        </BookInfoWrapper>
                    )}
                </SelectedContentWrapper>
            </MiddleContentWrapper>
            {creatingMode !== CreationModes.RECURRING_TASK && (
                <AdditionalInfoWrapper className={userInfo.isAnonymousUser ? "bottom-zero" : ""}>
                    <AdditionalInfoItem>
                        <WarningIconWrapper>
                            <StyledP6Icon name={"info"} />
                        </WarningIconWrapper>
                        <BookAvailabilityInfoDescWrapper>
                            <BookAvailabilityInfoTitle>{t_notAllBookTitle}</BookAvailabilityInfoTitle>
                            <BookAvailabilityInfoDesc>
                                {userInfo.isAnonymousUser ? t_notAvailableBookNotRegistered : t_notAvailableBook}
                            </BookAvailabilityInfoDesc>
                        </BookAvailabilityInfoDescWrapper>
                        {!userInfo.isAnonymousUser && !userInfo.hasNewsletterSubscription && (
                            <StyledSignUpButton
                                onClick={signupToNewsletter}
                                arrowDirection={"NONE"}
                                buttonStyle={"ORANGE_BLANK"}
                            >
                                {t_signUp}
                            </StyledSignUpButton>
                        )}
                    </AdditionalInfoItem>
                    {userTests.length === 0 && creatingMode === CreationModes.TEST && (
                        <AdditionalInfoItem>
                            <WarningIconWrapper>
                                <StyledP6Icon name={"cards-2"} />
                            </WarningIconWrapper>
                            <BookAvailabilityInfoDescWrapper>
                                <BookAvailabilityInfoDesc className={"bigger-font"}>
                                    {userInfo.isAnonymousUser ? t_ownContentAnonymous : t_useOwnContentInfo}
                                </BookAvailabilityInfoDesc>
                            </BookAvailabilityInfoDescWrapper>
                            {!userInfo.isAnonymousUser && (
                                <StyledButton
                                    arrowDirection={"NONE"}
                                    onClick={handleContinueOwnSubjectsClick}
                                >
                                    {t_continue}
                                </StyledButton>
                            )}
                        </AdditionalInfoItem>
                    )}
                </AdditionalInfoWrapper>
            )}
            {!userInfo.isAnonymousUser && (
                <CtasWrapperRT isSticky>
                    <ResponsiveButton
                        onClick={handleBackButton}
                        iconName="chevron-left"
                        label={t_back}
                    >
                        <StyledButton
                            onClick={handleBackButton}
                            arrowDirection={"LEFT"}
                            buttonStyle={"BLANK"}
                        >
                            {t_back}
                        </StyledButton>
                    </ResponsiveButton>

                    {creatingMode === CreationModes.RECURRING_TASK &&
                        currentExerciseSchoolClassId === "" &&
                        userOwnSubjects.length > 0 && (
                            <StyledButton
                                arrowDirection={"NONE"}
                                onClick={() => {
                                    // Switching to own content here must drop the book and its vocab
                                    // so they don't linger into the own-content flow.
                                    clearSchoolbookSelection();
                                    history.push("/create/select-own-source");
                                }}
                            >
                                {t_useOwnContent}
                            </StyledButton>
                        )}
                </CtasWrapperRT>
            )}
            <NoOwnContentModal
                isOpen={isNoOwnContentModalOpen}
                closeModalCallback={() => {
                    setIsNoOwnContentModalOpen(false);
                }}
                continueCallback={handleGoToWebAppClick}
            />
        </Wrapper>
    );
};

export default SelectBookDropdownsRT;
